/**
 * 문서 양식 관련 API 함수 모음
 * - 회사 코드에 해당하는 문서 양식 리스트를 서버에서 조회함
 * - withAuth: true 설정 시 로그인 시 저장된 accessToken, enumber, companyCode가 자동으로 헤더에 추가됨
 */
import apiClient from '@shared/api/apiClient';

/**
 * getDocFormList
 * - 문서 양식 리스트 조회 API
 * - 로그인 시 저장된 정보(accessToken, enumber, companyCode)를 헤더에 포함하여 요청
 * @param {number|string} companyCode - 회사 코드 (URL 경로 변수로 사용)
 * @returns {Promise<Object>} 서버에서 반환된 문서 양식 리스트 데이터
 */
async function getDocFormList(companyCode) {
  const response = await apiClient.get('/v1/forms/types', {
    params: { companyCode },
    withAuth: true, // 로그인 정보(accessToken, enumber, companyCode)를 헤더에 자동 추가
  });
  return response.data;
}

/**
 * getDocFormListSimple
 * - 문서 양식 리스트 조회 API (Simple 버전 - form_data 제외)
 * - form_data 필드를 제외한 간소화된 데이터를 반환함
 * - 로그인 시 저장된 정보(accessToken, enumber, companyCode)를 헤더에 포함하여 요청
 * @param {number|string} companyCode - 회사 코드 (URL 경로 변수로 사용)
 * @returns {Promise<Object>} 서버에서 반환된 문서 양식 리스트 데이터
 */
async function getDocFormListSimple(companyCode) {
  const response = await apiClient.get('/v1/forms/types/simple', {
    params: { companyCode },
    withAuth: true, // 로그인 정보(accessToken, enumber, companyCode)를 헤더에 자동 추가
  });
  return response.data;
}

/**
 * getDocFormListHtml
 * - 문서 양식 리스트 조회 API (HTML 버전 - form_data_html만 포함)
 * - form_data 대신 form_data_html 필드를 반환함
 * - 로그인 시 저장된 정보(accessToken, enumber, companyCode)를 헤더에 포함하여 요청
 * @param {number|string} companyCode - 회사 코드 (URL 경로 변수로 사용)
 * @returns {Promise<Object>} 서버에서 반환된 문서 양식 리스트 데이터
 */
async function getDocFormListHtml(companyCode) {
  const response = await apiClient.get('/v1/forms/types/html', {
    params: { companyCode },
    withAuth: true, // 로그인 정보(accessToken, enumber, companyCode)를 헤더에 자동 추가
  });
  return response.data;
}

/**
 * getDocFormListAdmin
 * - 문서 양식 리스트 조회 API (어드민 전용)
 * - admin에서 사용하는 api
 * - 로그인 시 저장된 정보(accessToken, enumber, companyCode)를 헤더에 포함하여 요청
 * @param {number|string} companyCode - 회사 코드 (URL 경로 변수로 사용)
 * @returns {Promise<Object>} 서버에서 반환된 문서 양식 리스트 데이터
 */
async function getDocFormListAdmin(companyCode) {
  const response = await apiClient.get('/v1/forms/types/admin', {
    params: { companyCode },
    withAuth: true, // 로그인 정보(accessToken, enumber, companyCode)를 헤더에 자동 추가
  });
  return response.data;
}

/**
 * updateForm
 * - 양식 수정 API
 * - 로그인 시 저장된 정보(accessToken, enumber, companyCode)를 헤더에 포함하여 요청
 * @param {Object} formData - 양식 데이터 (company, doc, title, form, form_data, useYn 등)
 * @returns {Promise<Object>} 서버 응답 데이터
 * @throws {Error} result_code가 SUCCESS(0)가 아닌 경우 에러 발생
 */
async function updateForm(formData) {
  const response = await apiClient.put('/v1/forms', formData, {
    withAuth: true, // 로그인 정보(accessToken, enumber, companyCode)를 헤더에 자동 추가
  });
  const data = response.data;
  // result_code가 SUCCESS(0)가 아니면 에러로 처리
  if (data.result_code !== 0) {
    const error = new Error(data.error_msg || '양식 수정 중 오류가 발생했습니다.');
    error.response = { data };
    throw error;
  }
  return data;
}

/**
 * updateFormSort
 * - 양식 정렬 수정 API
 * - 두 양식의 sort 값을 교환
 * - 로그인 시 저장된 정보(accessToken, enumber, companyCode)를 헤더에 포함하여 요청
 * @param {Object} sortData - 정렬 데이터 (company, doc_a, sort_a, doc_b, sort_b)
 * @returns {Promise<Object>} 서버 응답 데이터
 */

/**
 * updateFormsBatch
 * - 양식 일괄 수정 API (배치)
 * - 여러 양식의 정보를 한 번의 요청으로 수정
 * - 로그인 시 저장된 정보(accessToken, enumber, companyCode)를 헤더에 포함하여 요청
 * @param {Array} formDataList - 양식 데이터 목록
 * @returns {Promise<Object>} 서버 응답 데이터
 */
async function updateFormsBatch(formDataList) {
  const response = await apiClient.put('/v1/forms/batch', formDataList, {
    withAuth: true, // 로그인 정보(accessToken, enumber, companyCode)를 헤더에 자동 추가
  });
  return response.data;
}

/**
 * insertForm
 * - 양식 추가 API
 * - 로그인 시 저장된 정보(accessToken, enumber, companyCode)를 헤더에 포함하여 요청
 * @param {Object} formData - 양식 데이터 (company, title, form, form_data, required_docs, senderList, executorList, checkEmp)
 * @returns {Promise<Object>} 서버 응답 데이터
 * @throws {Error} result_code가 SUCCESS(0)가 아닌 경우 에러 발생
 */
async function insertForm(formData) {
  const response = await apiClient.post('/v1/forms', formData, {
    withAuth: true, // 로그인 정보(accessToken, enumber, companyCode)를 헤더에 자동 추가
  });
  const data = response.data;
  // result_code가 SUCCESS(0)가 아니면 에러로 처리
  if (data.result_code !== 0) {
    const error = new Error(data.error_msg || '양식 추가 중 오류가 발생했습니다.');
    error.response = { data };
    throw error;
  }
  return data;
}

/**
 * deleteForm
 * - 양식 삭제 API
 * - 로그인 시 저장된 정보(accessToken, enumber, companyCode)를 헤더에 포함하여 요청
 * @param {number} docId - 문서 타입 ID
 * @param {number} companyCode - 회사 코드
 * @returns {Promise<Object>} 서버 응답 데이터
 */
async function deleteForm(docId, companyCode) {
  const response = await apiClient.delete(`/v1/forms/${docId}`, {
    params: { company: companyCode },
    withAuth: true, // 로그인 정보(accessToken, enumber, companyCode)를 헤더에 자동 추가
  });
  return response.data;
}

/**
 * getFormData
 * - 양식 단일 조회 API (편집용)
 * - getDocFormList를 사용하여 전체 목록에서 docId에 해당하는 양식만 필터링
 * - 로그인 시 저장된 정보(accessToken, enumber, companyCode)를 헤더에 포함하여 요청
 * @param {number|string} companyCode - 회사 코드
 * @param {number} docId - 문서 타입 ID
 * @returns {Promise<Object>} 서버에서 반환된 양식 데이터
 */
async function getFormData(companyCode, docId) {
  const response = await apiClient.get('/v1/forms/types', {
    params: { companyCode },
    withAuth: true, // 로그인 정보(accessToken, enumber, companyCode)를 헤더에 자동 추가
  });
  // 응답에서 docId에 해당하는 양식만 필터링하여 반환
  const formList = response.data?.resultData?.docTypeList || [];
  const formData = formList.find((form) => form.doc === docId);
  return formData ? { resultData: { formData } } : { resultData: { formData: null } };
}

/**
 * getFormReceiverList
 * - 양식 참조자 목록 조회 API
 * - 로그인 시 저장된 정보(accessToken, enumber, companyCode)를 헤더에 포함하여 요청
 * @param {number} docId - 문서 타입 ID
 * @param {number} companyCode - 회사 코드
 * @returns {Promise<Object>} 서버에서 반환된 참조자 목록 데이터
 */
async function getFormReceiverList(docId, companyCode) {
  const response = await apiClient.post(
    '/v1/forms/receivers',
    {
      doc: docId,
      company: companyCode,
    },
    {
      withAuth: true, // 로그인 정보(accessToken, enumber, companyCode)를 헤더에 자동 추가
    },
  );
  return response.data;
}

/**
 * getFormExecutorList
 * - 양식 시행자 목록 조회 API
 * - 로그인 시 저장된 정보(accessToken, enumber, companyCode)를 헤더에 포함하여 요청
 * @param {number} docId - 문서 타입 ID
 * @param {number} companyCode - 회사 코드
 * @returns {Promise<Object>} 서버에서 반환된 시행자 목록 데이터
 */
async function getFormExecutorList(docId, companyCode) {
  const response = await apiClient.post(
    '/v1/forms/executors',
    {
      doc: docId,
      company: companyCode,
    },
    {
      withAuth: true, // 로그인 정보(accessToken, enumber, companyCode)를 헤더에 자동 추가
    },
  );
  return response.data;
}

/**
 * getApprovalLineGuideByDoc
 * - doc 기준 결재라인 가이드라인 조회 API (단일 - 하위 호환성 유지)
 * - 로그인 시 저장된 정보(accessToken, enumber, companyCode)를 헤더에 포함하여 요청
 * @param {number} docId - 문서 타입 ID
 * @param {number} companyCode - 회사 코드
 * @returns {Promise<Object>} 서버에서 반환된 가이드라인 데이터
 */
async function getApprovalLineGuideByDoc(docId, companyCode) {
  const response = await apiClient.get(`/v1/forms/${docId}/guide`, {
    params: { company: companyCode },
    withAuth: true, // 로그인 정보(accessToken, enumber, companyCode)를 헤더에 자동 추가
  });
  return response.data;
}

/**
 * getApprovalLineGuidesByDoc
 * - doc 기준 결재라인 가이드라인 조회 API (여러 개)
 * - 로그인 시 저장된 정보(accessToken, enumber, companyCode)를 헤더에 포함하여 요청
 * @param {number} docId - 문서 타입 ID
 * @param {number} companyCode - 회사 코드
 * @returns {Promise<Object>} 서버에서 반환된 가이드라인 데이터 목록
 */
async function getApprovalLineGuidesByDoc(docId, companyCode) {
  const response = await apiClient.get(`/v1/forms/${docId}/guides`, {
    params: { company: companyCode },
    withAuth: true, // 로그인 정보(accessToken, enumber, companyCode)를 헤더에 자동 추가
  });
  return response.data;
}

/**
 * getDocFormCheckEmp
 * - 양식 검토자 조회 API
 * - 로그인 시 저장된 정보(accessToken, enumber, companyCode)를 헤더에 포함하여 요청
 * @param {number} docId - 문서 타입 ID
 * @returns {Promise<Object>} 서버에서 반환된 검토자 데이터 (check1, check2, check3, check4)
 */
async function getDocFormCheckEmp(docId) {
  const response = await apiClient.get(`/v1/forms/${docId}/check-emp`, {
    withAuth: true, // 로그인 정보(accessToken, enumber, companyCode)를 헤더에 자동 추가
  });
  return response.data;
}

export {
  getDocFormList,
  getDocFormListSimple,
  getDocFormListHtml,
  getDocFormListAdmin,
  updateForm,
  updateFormsBatch,
  insertForm,
  deleteForm,
  getFormData,
  getFormReceiverList,
  getFormExecutorList,
  getApprovalLineGuideByDoc,
  getApprovalLineGuidesByDoc,
  getDocFormCheckEmp,
};
